import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Dumbbell, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Empty, TabHeading } from "./shared";

export default function ProgramsTab({ athleteId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs?athlete_id=${athleteId}`);
      setItems(data);
    } catch { toast.error("Failed to load programs."); } finally { setLoading(false); }
  }, [athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setCompletion = async (id, completion) => {
    try {
      await api.patch(`/programs/${id}`, { completion });
      setItems((p) => p.map((x) => (x.id === id ? { ...x, completion } : x)));
    } catch { toast.error("Failed to update."); }
  };

  const del = async (id) => {
    try { await api.delete(`/programs/${id}`); toast.success("Program removed."); load(); }
    catch { toast.error("Delete failed."); }
  };

  if (loading) return <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="af-programs">
      <TabHeading>Training Programs</TabHeading>
      {items.length === 0 ? (
        <Empty testid="af-programs-empty">No programs assigned yet. Assign one from Quick Actions.</Empty>
      ) : (
        <div className="space-y-4" data-testid="af-programs-list">
          {items.map((p) => (
            <div key={p.id} data-testid={`af-program-${p.id}`} className="bg-[#121212] border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Dumbbell size={16} className="text-white/50" />
                <p className="font-display font-bold uppercase tracking-tight flex-1">Week {p.week} · {p.title}</p>
                <button data-testid={`af-program-del-${p.id}`} onClick={() => del(p.id)} className="text-white/30 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
              </div>
              {p.instructions && <p className="text-white/70 text-sm mb-2">{p.instructions}</p>}
              {(p.items || []).length > 0 && (
                <ul className="text-white/70 text-sm list-disc list-inside mb-3 space-y-0.5">
                  {p.items.map((it, i) => <li key={`${i}-${typeof it === "string" ? it : it.name || i}`}>{typeof it === "string" ? it : it.name || JSON.stringify(it)}</li>)}
                </ul>
              )}
              {p.homework && <p className="text-white/50 text-xs mb-3"><span className="font-mono-accent tracking-widest uppercase text-white/40">Homework:</span> {p.homework}</p>}
              <div className="flex items-center gap-3">
                <input data-testid={`af-program-slider-${p.id}`} type="range" min="0" max="100" value={p.completion || 0} onChange={(e) => setCompletion(p.id, parseInt(e.target.value, 10))} className="flex-1 accent-white" />
                <span className="font-mono-accent text-sm font-bold w-12 text-right">{p.completion || 0}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
